import type { ModActionBody } from "@cfg/api-contract";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useApp } from "../app-context.tsx";
import { useAuthUser } from "../auth.ts";
import { useMe } from "../me.ts";
import { DefaultEmptyState } from "./defaults.tsx";

/** Staff-only queue of reported tags (one row per tag on a community entry). */
export function ModerationPage() {
  const { api, auth, brand, copy, locale } = useApp();
  const user = useAuthUser(auth);
  const Empty = brand.slots?.EmptyState ?? DefaultEmptyState;
  const me = useMe();
  const qc = useQueryClient();
  const isStaff = me.data?.plan === "staff";
  const queue = useQuery({ queryKey: ["modq", brand.config.id], queryFn: () => api!.modQueue(), enabled: !!api && isStaff });
  const [error, setError] = useState<string | null>(null);

  // Signed out the profile is never fetched, so decide on the user first.
  if (!user && auth.enabled)
    return (
      <Empty
        title={copy("mod.staffOnly")}
        action={
          <button type="button" className="cfg-btn" onClick={() => void auth.signIn()}>
            {copy("auth.signIn")}
          </button>
        }
      />
    );
  if (!api || !user || (me.isFetched && !isStaff)) return <Empty title={copy("mod.staffOnly")} />;
  if (queue.isLoading || me.isLoading) return <p className="muted page">{copy("common.loading")}</p>;

  const act = async (id: string, ref: string, action: ModActionBody["action"]) => {
    if (action === "remove" && !window.confirm(copy("mod.removeConfirm"))) return;
    setError(null);
    try {
      await api.modAction(id, ref, { action });
      await qc.invalidateQueries({ queryKey: ["modq"] });
      await qc.invalidateQueries({ queryKey: ["design-community"] });
    } catch (err) {
      setError((err as Error).message);
    }
  };
  const items = queue.data?.items ?? [];

  return (
    <section className="page page-mod">
      <header className="page-head">
        <h1>{copy("mod.title")}</h1>
      </header>
      {error && <p className="cfg-error">{error}</p>}
      {items.length === 0 ? (
        <Empty title={copy("mod.empty")} />
      ) : (
        <ul className="mine-list">
          {items.map((it) => (
            <li key={`${it.id}#${it.ref}`} className="mine-item mod-item">
              <div>
                <strong>{it.ref}</strong>
                {it.name && <span> {it.name}</span>}{" "}
                <span className={`version-pill${it.status === "hidden" ? " is-warn" : ""}`}>{copy(`mod.status.${it.status}`)}</span>
                <span className="muted small">
                  {" "}
                  {it.designId} · {copy("community.taggedBy", { name: it.owner })} · {copy("mod.reports", { count: it.reportCount, date: new Date(it.firstReportAt).toLocaleString(locale) })}
                </span>
                <ul className="mod-reports">
                  {it.reports.map((r, i) => (
                    <li key={i} className="small">
                      <strong>{copy(`community.reason.${r.reason}`)}</strong> {r.note && <span className="muted">“{r.note}”</span>}
                    </li>
                  ))}
                </ul>
                {it.status === "live" && (
                  <a className="small" href={`/design/${encodeURIComponent(it.designId)}?community=${encodeURIComponent(it.id)}`}>
                    {copy("mod.view")}
                  </a>
                )}
              </div>
              <div className="panel-save-row">
                {it.status === "live" ? (
                  <button type="button" className="cfg-chip" onClick={() => act(it.id, it.ref, "hide")}>
                    {copy("mod.hide")}
                  </button>
                ) : (
                  <button type="button" className="cfg-chip" onClick={() => act(it.id, it.ref, "restore")}>
                    {copy("mod.restore")}
                  </button>
                )}
                <button type="button" className="cfg-chip" onClick={() => act(it.id, it.ref, "dismiss")} title={copy("mod.dismissHint")}>
                  {copy("mod.dismiss")}
                </button>
                <button type="button" className="cfg-chip" onClick={() => act(it.id, it.ref, "remove")}>
                  {copy("mod.remove")}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
